'use client';

import { useCallback, useEffect, useState } from 'react';
import {
	BigNum,
	BASE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
} from '@drift-labs/sdk';
import { MarketId } from '@drift/common';
import ValueWithLabelCard from 'src/components/primitives/ValueWithLabelCard/ValueWithLabelCard';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useTvl from 'src/hooks/useTvl';
import useDriftClient from 'src/hooks/useDriftClient';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import { usePerpMarketsLookup } from 'src/hooks/perpMarkets/usePerpMarketsLookup';
import { useGetMarkPriceForMarket } from 'src/hooks/useMarkPrice';
import { PRICE_FALLBACK_DISPLAY_VALUE } from 'src/components/TradeForm/consts';

const StatsOverviewSection = () => {
	const driftClient = useDriftClient();
	const driftClientIsReady = useDriftClientIsReady();

	const [total24hQuoteVolume, setTotal24hQuoteVolume] = useState(0);
	const [total30dQuoteVolume, setTotal30dQuoteVolume] = useState(0);
	const [totalQuoteOI, setTotalQuoteOI] = useState(0);

	const [isTotal24hVolumeLoading, setIstotal24hVolumeLoading] = useState(true);
	const [isTotal30dVolumeLoading, setIstotal30dVolumeLoading] = useState(true);
	const [isTotalOILoading, setIsTotalOiLoading] = useState(true);

	const allMarketsData = useDriftStore((s) => s.marketsData24H);
	const tvl = useTvl();
	const perpMarketsLookup = usePerpMarketsLookup();
	const getMarkPrice = useGetMarkPriceForMarket();

	// Update 24h and 30d volume
	useEffect(() => {
		const getAndSetVolumeData = async () => {
			if (!allMarketsData || allMarketsData.length === 0) return;

			try {
				const totalQuoteVolume = allMarketsData.reduce((prev, next) => {
					return prev + next.quoteVolume;
				}, 0);
				setTotal24hQuoteVolume(totalQuoteVolume);
				setIstotal24hVolumeLoading(false);

				const totalQuoteVolume30D = allMarketsData.reduce((prev, next) => {
					return prev + next.quoteVolume30D;
				}, 0);
				setTotal30dQuoteVolume(totalQuoteVolume30D);
				setIstotal30dVolumeLoading(false);
			} catch (err) {
				console.log('Error parsing 24 hour perp markets data: ', err);
			}
		};

		getAndSetVolumeData();

		const interval = setInterval(getAndSetVolumeData, 1000 * 60);

		return () => {
			clearInterval(interval);
		};
	}, [allMarketsData]);

	// Update Total Open Interest
	const getAndSetUpdatedOI = useCallback(() => {
		if (!driftClientIsReady) {
			return;
		}

		let _totalQuoteOI = 0;

		perpMarketsLookup.forEach((m) => {
			const marketId = MarketId.createPerpMarket(m.marketIndex);
			const market = driftClient?.getPerpMarketAccount(marketId.marketIndex);

			if (!market) return;

			const OI = BigNum.from(
				market.amm.baseAssetAmountLong.add(
					market.amm.baseAssetAmountShort.abs()
				),
				BASE_PRECISION_EXP
			);

			const markPrice =
				getMarkPrice(marketId) ?? BigNum.zero(QUOTE_PRECISION_EXP);

			const quoteOIforMarket = markPrice.toNum() * OI.toNum();
			_totalQuoteOI += quoteOIforMarket;
		});

		setTotalQuoteOI(_totalQuoteOI);
		setIsTotalOiLoading(false);
	}, [driftClient, driftClientIsReady, perpMarketsLookup, getMarkPrice]);

	// Update OI every few seconds
	useEffect(() => {
		getAndSetUpdatedOI();
		const interval = setInterval(getAndSetUpdatedOI, 5000);
		return () => clearInterval(interval);
	}, [getAndSetUpdatedOI]);

	const formatValue = (value: number, isLoading: boolean) => {
		if (isLoading || value === 0) {
			return PRICE_FALLBACK_DISPLAY_VALUE;
		}
		return `$${BigNum.fromPrint(
			value.toString(),
			QUOTE_PRECISION_EXP
		).toMillified()}`;
	};

	// Check if deposits are still loading (when it's zero or very small)
	const isDepositDataLoading =
		tvl.depositTvl.eqZero() || tvl.depositTvl.toNum() < 1;

	return (
		<ValueWithLabelCard.Container
			className="w-[63.5vw] max-sm:w-full max-sm:flex-col max-sm:gap-y-3 max-sm:p-md"
			data-testid="stats-overview-section"
		>
			<div className="max-sm:flex max-sm:gap-x-3xl sm:contents">
				<ValueWithLabelCard.Item
					className="max-sm:w-[50%] max-sm:flex-1 sm:w-[22%]"
					label="24H Vol"
				>
					{formatValue(total24hQuoteVolume, isTotal24hVolumeLoading)}
				</ValueWithLabelCard.Item>
				<ValueWithLabelCard.Item
					className="max-sm:w-[50%] max-sm:flex-1 sm:w-[22%]"
					label="30D Vol"
				>
					{formatValue(total30dQuoteVolume, isTotal30dVolumeLoading)}
				</ValueWithLabelCard.Item>
			</div>
			<div className="max-sm:flex max-sm:gap-x-3xl sm:contents">
				<ValueWithLabelCard.Item
					className="max-sm:w-[50%] max-sm:flex-1 sm:w-[22%]"
					label="Total OI"
				>
					{formatValue(totalQuoteOI, isTotalOILoading)}
				</ValueWithLabelCard.Item>
				<ValueWithLabelCard.Item
					className="max-sm:w-[50%] max-sm:flex-1 sm:w-[22%]"
					label="Deposits"
				>
					{isDepositDataLoading ? '---' : `$${tvl.depositTvl.toMillified()}`}
				</ValueWithLabelCard.Item>
			</div>
		</ValueWithLabelCard.Container>
	);
};

export default StatsOverviewSection;
